import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { clientCache } from '@/utils/clientCache'
import { authenticationApi } from '@/api/authenticationApi'
import { userApi } from '@/api/userApi'

const CACHE_KEY = 'user'
const ONE_DAY_IN_SECONDS = 24 * 60 * 60

export interface UserData {
    userName: string
    interfaceLanguage: string
    externalTranslationLimit: number
    isGroupAdmin: boolean
    subscriptionManagedByStripe: boolean
    excelTranslationAccess: boolean
    powerpointTranslationAccess: boolean
    idmlTranslationAccess: boolean
}

export const useUserStore = defineStore('user', () => {

    // --- State ---

    const user = ref<UserData | null>(getUserFromCache())
    const isLoading = ref(false)

    // --- Getters ---

    const userName = computed(() => user.value?.userName ?? '')
    const interfaceLanguage = computed(() => user.value?.interfaceLanguage ?? '')
    const externalTranslationLimit = computed(() => user.value?.externalTranslationLimit ?? 0)
    const isGroupAdmin = computed(() => user.value?.isGroupAdmin ?? false)
    const subscriptionManagedByStripe = computed(() => user.value?.subscriptionManagedByStripe ?? false)
    const hasExcelAccess = computed(() => user.value?.excelTranslationAccess ?? false)
    const hasPowerpointAccess = computed(() => user.value?.powerpointTranslationAccess ?? false)
    const hasIdmlAccess = computed(() => user.value?.idmlTranslationAccess ?? false)
    const isLoggedIn = computed(() => user.value !== null)

    // --- Actions ---  

    async function login(accessToken: string) {
        await authenticationApi.userLogin(accessToken)
        await loadUser()
    }

    async function logout() {
        await authenticationApi.userLogout()
        clearUser()
    }

    async function ensureLoaded(): Promise<UserData | null> {
        if (user.value !== null) {
            return user.value
        }
        return await loadUser()
    }

    async function setLanguage(language: string) {
        const updatedUser = await userApi.setInterfaceLanguage(language)
        if (updatedUser != null) {
            setUserToCache(updatedUser)
            user.value = updatedUser
        }
    }

    async function getUserSubscription(): Promise<any> {
        return await userApi.getUserSubscription()
    }

    // --- Private helpers ---

    async function loadUser(): Promise<UserData | null> {
        isLoading.value = true
        try {
            const loadedUser = await userApi.getUserInfo()
            if (loadedUser != null) {
                setUserToCache(loadedUser)
                user.value = loadedUser
            }
            return loadedUser
        } finally {
            isLoading.value = false
        }
    }

    function clearUser() {
        user.value = null
        removeUserFromCache()
    }

    // --- Cache operations ---

    function setUserToCache(value: any) {
        clientCache.set(CACHE_KEY, value, ONE_DAY_IN_SECONDS)
    }

    function removeUserFromCache() {
        clientCache.remove(CACHE_KEY)
    }

    return {
        user,
        isLoading,
        userName,
        interfaceLanguage,
        externalTranslationLimit,
        isGroupAdmin,
        subscriptionManagedByStripe,
        hasExcelAccess,
        hasPowerpointAccess,
        hasIdmlAccess,
        isLoggedIn,
        login,
        logout,
        ensureLoaded,
        setLanguage,
        getUserSubscription,
    }
})

/**
 * Reads user from cache directly, without Pinia.
 * Safe to call before Pinia is initialized (e.g. during i18n setup).
 */
export function getUserFromCache(): UserData | null {
    return clientCache.get(CACHE_KEY)
}